// ═══════════════════════════════════════════════════════════════
// spark_viewer.js — Viewer partagé, importable par chaque page
// Usage : import { initViewer } from './spark_viewer.js';
//         initViewer(CONFIG);
// ═══════════════════════════════════════════════════════════════

import * as THREE from "three";
import { SplatMesh } from "@sparkjsdev/spark";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { GUI } from "lil-gui";

export async function initViewer(viewerConfig = {}) {

  // ── Résolution des variables de config ──────────────────────
  const lieuCible  = viewerConfig.lieuCible  || document.title;
  const splatURL   = viewerConfig.splatURL   || null;
  const meshURL    = viewerConfig.meshURL    || null;
  const planURL    = viewerConfig.planURL    || null;

  // ── Scène ────────────────────────────────────────────────────
  const scene  = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(renderer.domElement);

  // ── Contrôles ────────────────────────────────────────────────
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(
    viewerConfig.camTargetX ?? 0.2,
    viewerConfig.camTargetY ?? 0,
    viewerConfig.camTargetZ ?? 0
  );
  controls.minDistance  = viewerConfig.camMinDistance ?? 0.1;
  controls.maxDistance  = viewerConfig.camMaxDistance ?? 10;
  controls.enabled      = true;
  controls.rotateSpeed  = 0.5;
  controls.enableDamping  = true;
  controls.dampingFactor  = 0.08;
  controls.zoomToCursor   = true;

  camera.position.set(
    viewerConfig.camPosX ?? 0,
    viewerConfig.camPosY ?? 0,
    viewerConfig.camPosZ ?? 1
  );
  camera.rotation.set(
    viewerConfig.camRotX ?? 0,
    viewerConfig.camRotY ?? 0,
    viewerConfig.camRotZ ?? 0
  );
  controls.update();

  // ── Splat ────────────────────────────────────────────────────
  const modele = new SplatMesh({ url: splatURL });
  modele.quaternion.set(1, 0, 0, 0);
  modele.position.set(0, 0, 0);
  scene.add(modele);

  // ── Mesh optionnel ───────────────────────────────────────────
  if (meshURL !== null) {
    const loader    = new GLTFLoader();
    const puitsMesh = await loader.loadAsync(meshURL);
    const wfMaterial = new THREE.MeshBasicMaterial({ color: 0xFFAA55, wireframe: true });
    puitsMesh.scene.traverse((child) => {
      if (child.isMesh) child.material = wfMaterial;
    });
    scene.add(puitsMesh.scene);
  }

  // ── Plan SVG ─────────────────────────────────────────────────
  let planeMesh;

  async function loadSVGPlane(svgURL) {
    const response = await fetch(svgURL);
    const svgText  = await response.text();

    const parser     = new DOMParser();
    const svgDoc     = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    let width  = parseFloat(svgElement.getAttribute('width'))  || 1024;
    let height = parseFloat(svgElement.getAttribute('height')) || 1024;

    if (!svgElement.getAttribute('width')) {
      const viewBox = svgElement.getAttribute('viewBox');
      if (viewBox) {
        const parts = viewBox.split(' ');
        width  = parseFloat(parts[2]) || 1024;
        height = parseFloat(parts[3]) || 1024;
      }
    }

    const canvasWidth  = width  * 4;
    const canvasHeight = height * 4;

    const canvas = document.createElement('canvas');
    canvas.width  = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const img  = new Image();
    const blob = new Blob([svgText], { type: 'image/svg+xml' });
    const url  = URL.createObjectURL(blob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      const texture = new THREE.CanvasTexture(canvas);
      texture.magFilter = THREE.LinearFilter;
      texture.minFilter = THREE.LinearFilter;

      const planeGeometry = new THREE.PlaneGeometry(184.25, 56);
      const planeMaterial = new THREE.MeshBasicMaterial({
        map: texture,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 1
      });
      planeMesh = new THREE.Mesh(planeGeometry, planeMaterial);
      planeMesh.position.set(-0.9, -1.5, 34.2);
      planeMesh.rotation.set(-87 * (Math.PI / 180), 0, 96 * (Math.PI / 180));
      scene.add(planeMesh);
      URL.revokeObjectURL(url);
    };

    img.src = url;
  }

  loadSVGPlane(planURL);

  // ── GUI ──────────────────────────────────────────────────────
  let preset = {};

  const gui = new GUI({
    title: "Paramètres",
    container: document.getElementById("main-gui")
  });
    gui.close();


  const guiOptions = {
    bgColor: "#000000",
    showLabels: true,
    labelSize: 1.0,
    labelOpacity: 1.0,
    distanceThreshold: false,
    camPosX: camera.position.x,
    camPosY: camera.position.y,
    camPosZ: camera.position.z,
    camRotX: 0,
    camRotY: 0,
    camRotZ: 0,
    showPlane: true,
    planeScale: 1,
    planeMaterialOpacity: 1.0,
    posX: -0.9,
    posY: -1.5,
    posZ: 34.2,
    rotX: -87,
    rotY: 0,
    rotZ: 96,
    savePreset() { preset = gui.save(); },
    loadPreset() { gui.load(preset); }
  };

  gui.addColor(guiOptions, "bgColor").name("Couleur de fond").onChange((value) => {
    scene.background = new THREE.Color(value);
  });

  const cameraFolder = gui.addFolder("Camera");
  cameraFolder.add(camera, "fov", 1, 179, 0.5).name("Champs de vision").listen().onChange(() => {
    camera.updateProjectionMatrix();
  });
  cameraFolder.add(guiOptions, "distanceThreshold").name("Déverrouiller la distance max").onChange(() => {
    controls.maxDistance = guiOptions.distanceThreshold ? 999999 : 100;
  });

  const cameraPose = cameraFolder.addFolder("Camera Pose");
  cameraPose.add(camera.position, "x", -100, 100, 0.01).name("X").listen().decimals(3);
  cameraPose.add(camera.position, "y", -100, 100, 0.01).name("Y").listen().decimals(3);
  cameraPose.add(camera.position, "z", -100, 100, 0.01).name("Z").listen().decimals(3);

  const cameraSpecs = cameraPose.addFolder("Camera Specs");
  cameraSpecs.add(camera.rotation, "x", -Math.PI, Math.PI, 0.01).name("RotateX").listen().decimals(3);
  cameraSpecs.add(camera.rotation, "y", -Math.PI, Math.PI, 0.01).name("RotateY").listen().decimals(3);
  cameraSpecs.add(camera.rotation, "z", -Math.PI, Math.PI, 0.01).name("RotateZ").listen().decimals(3);
  cameraSpecs.add(controls.target, "x", -100, 100, 0.01).name("Target X").listen().decimals(3);
  cameraSpecs.add(controls.target, "y", -100, 100, 0.01).name("Target Y").listen().decimals(3);
  cameraSpecs.add(controls.target, "z", -100, 100, 0.01).name("Target Z").listen().decimals(3);
  cameraSpecs.close();
  cameraPose.close();

  const planFolder = gui.addFolder("Plan");
  planFolder.add(guiOptions, "showPlane").name("Visible").onChange(updatePlaneVisibility);
  planFolder.add(guiOptions, "planeMaterialOpacity", 0, 1, 0.01).name("Opacity").onChange((value) => {
    if (planeMesh) planeMesh.material.opacity = value;
  });

  const planTransform = planFolder.addFolder("Transformer");
  planTransform.close();

  planTransform.add(guiOptions, "planeScale", 0.1, 5, 0.1).name("Scale").listen().onChange((value) => {
    if (planeMesh) { planeMesh.scale.x = value; planeMesh.scale.y = value; }
  });
  planTransform.add(guiOptions, "posX", -10, 10, 0.1).name("Pos X").listen().onChange((v) => { if (planeMesh) planeMesh.position.x = v; });
  planTransform.add(guiOptions, "posY", -10, 10, 0.1).name("Pos Y").listen().onChange((v) => { if (planeMesh) planeMesh.position.y = v; });
  planTransform.add(guiOptions, "posZ", -10, 10, 0.1).name("Pos Z").listen().onChange((v) => { if (planeMesh) planeMesh.position.z = v; });
  planTransform.add(guiOptions, "rotX", -180, 180, 1).name("Rot X (deg)").listen().onChange((v) => { if (planeMesh) planeMesh.rotation.x = v * (Math.PI / 180); });
  planTransform.add(guiOptions, "rotY", -180, 180, 1).name("Rot Y (deg)").listen().onChange((v) => { if (planeMesh) planeMesh.rotation.y = v * (Math.PI / 180); });
  planTransform.add(guiOptions, "rotZ", -180, 180, 1).name("Rot Z (deg)").listen().onChange((v) => { if (planeMesh) planeMesh.rotation.z = v * (Math.PI / 180); });
  planTransform.add({ reset: resetPlaneTransform }, "reset").name("Reset Position/Rotation");

  function resetPlaneTransform() {
    guiOptions.posX = -0.9; guiOptions.posY = -1.5; guiOptions.posZ = 34.2;
    guiOptions.rotX = -87;  guiOptions.rotY = 0;    guiOptions.rotZ = 96;
    if (planeMesh) {
      planeMesh.position.set(guiOptions.posX, guiOptions.posY, guiOptions.posZ);
      planeMesh.rotation.set(
        guiOptions.rotX * (Math.PI / 180),
        guiOptions.rotY * (Math.PI / 180),
        guiOptions.rotZ * (Math.PI / 180)
      );
    }
  }
  planFolder.close();

  // Exporter preset
  gui.add({ export: exportPreset }, "export").name("Exporter Preset");
  function exportPreset() {
    preset = gui.save();
    const json = JSON.stringify(preset, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    const now  = new Date();
    const yyyy = now.getFullYear();
    const mm   = String(now.getMonth() + 1).padStart(2, '0');
    const dd   = String(now.getDate()).padStart(2, '0');
    const hh   = String(now.getHours()).padStart(2, '0');
    const min  = String(now.getMinutes()).padStart(2, '0');
    a.download = `preset-${lieuCible || 'scan'}-${yyyy}-${mm}-${dd}_${hh}-${min}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Importer preset
  gui.add({ import: importPreset }, "import").name("Importer Preset");
  function importPreset() {
    const input   = document.createElement('input');
    input.type    = 'file';
    input.accept  = 'application/json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          preset = JSON.parse(event.target.result);
          gui.load(preset);
        } catch (err) {
          console.error('Error importing preset:', err);
          alert('Failed to import preset. Check the file format.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  // Export PNG
  gui.add({ exportView: exportViewAsPNG }, "exportView").name("Export View as PNG");
  function exportViewAsPNG() {
    const origWidth  = renderer.domElement.width;
    const origHeight = renderer.domElement.height;
    renderer.setSize(origWidth * 2, origHeight * 2);
    labelRenderer.setSize(origWidth * 2, origHeight * 2);
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
    renderer.domElement.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href    = url;
      const now   = new Date();
      const year  = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day   = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const mins  = String(now.getMinutes()).padStart(2, '0');
      a.download = `view_${year}-${month}-${day}_${hours}_${mins}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      renderer.setSize(origWidth, origHeight);
      labelRenderer.setSize(origWidth, origHeight);
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
    }, 'image/png');
  }

  function updatePlaneVisibility() {
    if (planeMesh) planeMesh.visible = guiOptions.showPlane;
  }

  // ── TransformWidget ──────────────────────────────────────────
  class TransformWidget {
    constructor(scene, camera, renderer, gui) {
      this.scene    = scene;
      this.camera   = camera;
      this.renderer = renderer;

      this.widgetGroup = new THREE.Group();
      this.widgetGroup.visible = false;
      this.widgetGroup.position.set(0, 0, 0);
      this.scene.add(this.widgetGroup);

      const axesHelper = new THREE.AxesHelper(1);
      this.widgetGroup.add(axesHelper);

      this.handle = document.createElement('div');
      Object.assign(this.handle.style, {
        position: 'absolute', width: '18px', height: '18px',
        borderRadius: '50%', background: 'rgba(255,0,0,0.8)',
        border: '2px solid #fff', boxSizing: 'border-box',
        pointerEvents: 'auto', cursor: 'grab', zIndex: '1001',
        transform: 'translate(-50%, -50%)', display: 'none'
      });
      document.body.appendChild(this.handle);

      this._raycaster = new THREE.Raycaster();
      this._plane     = new THREE.Plane();
      this._dragging  = false;
      this._pointerId = null;

      this.handle.addEventListener('pointerdown',  (e) => this._onHandlePointerDown(e));
      window.addEventListener('pointermove', (e) => this._onPointerMove(e));
      window.addEventListener('pointerup',   (e) => this._onPointerUp(e));

      this._updateLoop = () => {
        if (this.widgetGroup.visible) this._updateHandlePosition();
        this._rafId = requestAnimationFrame(this._updateLoop);
      };
      this._rafId = requestAnimationFrame(this._updateLoop);

      const toolsFolder = gui.addFolder('Tools');
      this.guiOptions = {
        widgetEnabled: false,
        posX: 0, posY: 0, posZ: 0,
        coordDisplay: '0.00, 0.00, 0.00',
        copyCoordinates: () => this.copyCoordinates(),
        resetPosition:   () => this.resetPosition()
      };

      toolsFolder.add(this.guiOptions, 'widgetEnabled').name('Afficher Repère 3D').onChange((v) => this.setVisibility(v));

      const positionFolder = toolsFolder.addFolder('Position');
      positionFolder.add(this.guiOptions, 'posX', -100, 100, 0.1).name('X').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.add(this.guiOptions, 'posY', -100, 100, 0.1).name('Y').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.add(this.guiOptions, 'posZ', -100, 100, 0.1).name('Z').listen().onChange(() => this.updatePositionFromInputs());
      positionFolder.close();

      toolsFolder.add(this.guiOptions, 'coordDisplay').name('Coordonnées').listen();
      toolsFolder.add(this.guiOptions, 'copyCoordinates').name('Copier les coordonnées');
      toolsFolder.add(this.guiOptions, 'resetPosition').name('Réinitialiser');
      toolsFolder.close();
    }

    setVisibility(value) {
      this.widgetGroup.visible = value;
      this.handle.style.display = value ? 'block' : 'none';
      this.updateDisplay();
    }

    updateDisplay() {
      const pos = this.widgetGroup.position;
      this.guiOptions.posX = parseFloat(pos.x.toFixed(2));
      this.guiOptions.posY = parseFloat(pos.y.toFixed(2));
      this.guiOptions.posZ = parseFloat(pos.z.toFixed(2));
      this.guiOptions.coordDisplay = `${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}`;
    }

    updatePositionFromInputs() {
      this.widgetGroup.position.set(
        parseFloat(this.guiOptions.posX),
        parseFloat(this.guiOptions.posY),
        parseFloat(this.guiOptions.posZ)
      );
      this.updateDisplay();
    }

    resetPosition() {
      this.widgetGroup.position.set(0, 0, 0);
      this.updateDisplay();
    }

    copyCoordinates() {
      navigator.clipboard.writeText(this.guiOptions.coordDisplay)
        .then(() => console.log('Coordonnées copiées:', this.guiOptions.coordDisplay))
        .catch(err => console.error('Erreur lors de la copie:', err));
    }

    _getPointerNDC(e) {
      const rect = this.renderer.domElement.getBoundingClientRect();
      return new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width)  * 2 - 1,
        -((e.clientY - rect.top)  / rect.height) * 2 + 1
      );
    }

    _onHandlePointerDown(e) {
      e.stopPropagation(); e.preventDefault();
      this._pointerId = e.pointerId;
      this.handle.setPointerCapture(this._pointerId);
      this.handle.style.cursor = 'grabbing';
      this._dragging = true;
      const camDir = new THREE.Vector3();
      this.camera.getWorldDirection(camDir).normalize();
      this._plane.setFromNormalAndCoplanarPoint(camDir, this.widgetGroup.position);
    }

    _onPointerMove(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      e.stopPropagation(); e.preventDefault();
      const ndc = this._getPointerNDC(e);
      this._raycaster.setFromCamera(ndc, this.camera);
      const intersect = new THREE.Vector3();
      if (this._raycaster.ray.intersectPlane(this._plane, intersect)) {
        this.widgetGroup.position.copy(intersect);
        this.updateDisplay();
        this._updateHandlePosition();
      }
    }

    _onPointerUp(e) {
      if (!this._dragging || e.pointerId !== this._pointerId) return;
      e.stopPropagation(); e.preventDefault();
      this.handle.releasePointerCapture(this._pointerId);
      this._pointerId = null;
      this._dragging  = false;
      this.handle.style.cursor = 'grab';
    }

    _updateHandlePosition() {
      const pos = this.widgetGroup.position.clone();
      pos.project(this.camera);
      this.handle.style.left = `${(pos.x *  0.5 + 0.5) * window.innerWidth}px`;
      this.handle.style.top  = `${(pos.y * -0.5 + 0.5) * window.innerHeight}px`;
    }
  }

  const widget = new TransformWidget(scene, camera, renderer, gui);

  // ── Données JSON + Labels ────────────────────────────────────
  const res  = await fetch('assets/_data/data_info_layer.json');
  const data = await res.json();
  console.log("JSON chargé");

  const labelRenderer = new CSS2DRenderer();
  labelRenderer.setSize(window.innerWidth, window.innerHeight);
  labelRenderer.domElement.style.position     = 'absolute';
  labelRenderer.domElement.style.top          = '0px';
  labelRenderer.domElement.style.pointerEvents = 'none';
  document.body.appendChild(labelRenderer.domElement);

  async function loadLabels(lieuCible) {
    const reponse        = await fetch('misc/templates/label.htm');
    const label_template = await reponse.text();
    const filtered       = data.filter(entry => entry.lieu === lieuCible);
    filtered.forEach(entry => createLabel(entry, label_template));
  }

  loadLabels(lieuCible);

  function createLabel(entry, template_html) {
    const label_import = document.createElement("div");
    label_import.innerHTML = template_html.trim();
    label_import.querySelector('.label_title').textContent = entry.titre;
    label_import.querySelector('.label_image').src = entry.image_label || '';
    const imgContainer = label_import.querySelector('.label_image_container');
    if (!entry.image_label) imgContainer.classList.add('hidden');
    else imgContainer.classList.remove('hidden');

    const posCible = new THREE.Vector3().fromArray(entry.coord_cible);
    const posLabel = new THREE.Vector3().fromArray(entry.coord_label);
    console.log("labels itérés");

    label_import.style.pointerEvents = 'auto';
    label_import.style.cursor        = 'pointer';
    const label_container = new CSS2DObject(label_import);
    label_container.position.copy(posLabel);

    label_import.addEventListener('click', () => {
      fillPopup(entry);
      popupEl.classList.add('open');
    });

    scene.add(label_container);

    const points      = [posCible, posLabel];
    const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineMaterial = new THREE.LineBasicMaterial({ color: 0xffffff });
    scene.add(new THREE.Line(lineGeometry, lineMaterial));

    const circleGeometry = new THREE.SphereGeometry(0.02, 16, 16);
    const circleMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const circle = new THREE.Mesh(circleGeometry, circleMaterial);
    circle.position.copy(posCible);
    scene.add(circle);
  }

  // ── Popup ────────────────────────────────────────────────────
  let popupEl;
  fetch('misc/templates/popup.htm')
    .then(res => res.text())
    .then(html => {
      const popUpContainer = document.createElement('div');
      popUpContainer.innerHTML = html.trim();
      const styleEl = popUpContainer.querySelector('style');
      if (styleEl) document.head.appendChild(styleEl);
      popupEl = popUpContainer.querySelector('#popupContainer');
      document.body.appendChild(popupEl);

      popupEl.querySelector('#popupClose').addEventListener('click', () => {
        popupEl.classList.remove('open');
        popupEl.classList.add('collapsed');
      });
      popupEl.querySelector('#popupHeader').addEventListener('click', (e) => {
        if (e.target.id === 'popupClose') return;
        popupEl.classList.add('open');
        popupEl.classList.remove('collapsed');
      });
    });

  function fillPopup(entry) {
    if (!popupEl) return;
    popupEl.querySelector('#popupTitle').textContent    = entry.titre_complet;
    popupEl.querySelector('#popupDate').textContent     = entry.date;
    popupEl.querySelector('#popupSubTitle').textContent = entry.sous_titre;
    popupEl.querySelector('#popupText').textContent     = entry.contenu;
    popupEl.classList.add('open');
    popupEl.classList.remove('collapsed');
  }

  // ── Resize ───────────────────────────────────────────────────
  function onWindowResize() {
    const w = window.innerWidth, h = window.innerHeight;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    labelRenderer.setSize(w, h);
    controls.update();
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
  }
  window.addEventListener('resize', onWindowResize, { passive: true });

  // ── Boucle d'animation ───────────────────────────────────────
  renderer.setAnimationLoop(function animate() {
    controls.update();
    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
  });
}
